const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface GroupMember {
  id: string;
  user: User;
  addedAt: string;
  addedBy: User;
}

export type GroupVisibility = 'public' | 'private';

export interface Group {
  id: string;
  name: string;
  description?: string;
  visibility: GroupVisibility;
  memberCount: number;
  createdBy: User;
  createdAt: string;
  isMember: boolean;
  isCreator: boolean;
  canManageMembers?: boolean;
  members?: GroupMember[];
}

export interface CreateGroupData {
  name: string;
  description?: string;
  visibility?: GroupVisibility;
}

export interface AddMembersResult {
  added: string[];
  alreadyMembers: string[];
  notFound: string[];
}

class GroupService {
  private getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
    };
  }

  // Search users in the company for autocomplete
  async searchUsers(query: string, limit: number = 10): Promise<User[]> {
    try {
      const params = new URLSearchParams({ q: query, limit: limit.toString() });
      const response = await fetch(`${API_BASE_URL}/api/groups/users/search?${params.toString()}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to search users');
      }

      const data = await response.json();
      return data.users;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  // Get all groups in the company
  async getGroups(): Promise<Group[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }

      const data = await response.json();
      return data.groups;
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  }

  // Get a specific group with members
  async getGroup(groupId: string): Promise<Group> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch group');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching group:', error);
      throw error;
    }
  }

  // Create a new group
  async createGroup(data: CreateGroupData): Promise<Group> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create group' }));
        throw new Error(errorData.error || 'Failed to create group');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }

  // Update a group
  async updateGroup(groupId: string, data: Partial<CreateGroupData>): Promise<Group> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update group' }));
        throw new Error(errorData.error || 'Failed to update group');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  }

  // Delete a group
  async deleteGroup(groupId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete group' }));
        throw new Error(errorData.error || 'Failed to delete group');
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  }

  // Add members to a group
  async addMembers(groupId: string, userIds: string[]): Promise<AddMembersResult> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ userIds })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to add members' }));
        throw new Error(errorData.error || 'Failed to add members');
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding members:', error);
      throw error;
    }
  }

  // Remove a member from a group
  async removeMember(groupId: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/members/${userId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to remove member' }));
        throw new Error(errorData.error || 'Failed to remove member');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  }
}

export const groupService = new GroupService();
